import { z } from 'zod'
import { DEFAULT_LINK_SECTIONS, LINK_SECTIONS } from '../constants/links.js'
import { clientBaseSchema, countryCodeSchema } from './clients.js'

/**
 * Slugs live in a public URL (`/r/<slug>`), so they're constrained to
 * lowercase, digits and hyphens — no case ambiguity when read off a flyer or
 * dictated over the phone.
 */
export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Slug must be at least 3 characters')
  .max(60, 'Slug must be at most 60 characters')
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and hyphens only')

/** Reserved because `/r/<slug>` must never collide with an app route. */
const RESERVED_SLUGS = new Set(['new', 'edit', 'admin', 'api', 'login', 'r', 'closed', 'preview'])

export const linkSlugSchema = slugSchema.refine(
  (v) => !RESERVED_SLUGS.has(v),
  'That slug is reserved — pick another',
)

const isoDateTime = z
  .string()
  .datetime({ offset: true })
  .or(z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/))

const optionalDateTime = z
  .union([z.literal(''), isoDateTime])
  .optional()
  .transform((v) => (v === '' ? undefined : v))

export const linkBaseSchema = z.object({
  title: z.string().trim().min(3, 'Title is required').max(150),
  description: z.string().trim().max(2000).optional(),
  slug: linkSlugSchema.optional(),
  opensAt: optionalDateTime,
  closesAt: optionalDateTime,
  active: z.boolean().default(true),
  maxRegistrations: z.coerce.number().int().min(1).max(100_000).nullable().optional(),
  /** Applied to every registrant — e.g. a "Romania jobs" link auto-tags Romania. */
  presetCountries: z.array(countryCodeSchema).max(10).default([]),
  presetCategories: z.array(z.string()).max(10).default([]),
  sections: z.array(z.enum(LINK_SECTIONS)).default([...DEFAULT_LINK_SECTIONS]),
})

const scheduleRule = <T extends z.ZodTypeAny>(schema: T) =>
  schema.superRefine((v: z.infer<typeof linkBaseSchema>, ctx: z.RefinementCtx) => {
    if (v.opensAt && v.closesAt && new Date(v.closesAt) <= new Date(v.opensAt)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['closesAt'],
        message: 'The close time must be after the open time',
      })
    }
  })

export const createLinkSchema = scheduleRule(linkBaseSchema)
export type CreateLinkInput = z.input<typeof linkBaseSchema>

export const updateLinkSchema = scheduleRule(linkBaseSchema.partial())
export type UpdateLinkInput = z.infer<typeof linkBaseSchema>

export const listLinksQuerySchema = z.object({
  includeInactive: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => v === true || v === 'true'),
})

/* -------------------------------------------------------------------------- */
/*  Public registration                                                        */
/* -------------------------------------------------------------------------- */

/**
 * What the public form posts. Built from the same `clientBaseSchema` the staff
 * form uses, minus the fields the public must never set:
 *  - assignedUserId / status / source — agency-side, decided by staff
 *  - preferred countries stay allowed, but link presets are merged on top
 *    server-side, so a tampered payload can't shed the link's tags.
 */
export const publicRegistrationSchema = clientBaseSchema.omit({ assignedUserId: true }).extend({
  /** Cloudflare Turnstile response; verified server-side before anything else. */
  turnstileToken: z.string().optional(),
})

export type PublicRegistrationInput = z.input<typeof publicRegistrationSchema>

/* -------------------------------------------------------------------------- */
/*  Review actions                                                             */
/* -------------------------------------------------------------------------- */

export const rejectClientSchema = z.object({
  reason: z.string().trim().min(1, 'Give a reason so the decision is auditable').max(500),
})
export type RejectClientInput = z.infer<typeof rejectClientSchema>

/**
 * Merge a pending registration into the client it duplicates. `targetId` is the
 * record that survives; the pending one is folded in and soft-deleted.
 */
export const mergeClientSchema = z.object({
  targetId: z.string().min(1),
})
export type MergeClientInput = z.infer<typeof mergeClientSchema>
